import React, { FormEvent, ReactElement, useCallback, useState } from 'react';
import { isEmpty } from 'lodash/fp';

import http from '../../utils/services/http';
import globalLogger from '../../logger';
import delay from '../../../common/utils/lib/delay';

import LoadingSpinner from '../../components/loaders/Spinner';

import '../components/AdminDashboardForm.scss';

const logger = globalLogger.createChannel('admin.app-versions');
import { AxiosError } from 'axios';
import { METHODS } from '../../../common/utils/http/httpConstants';
import {
    AppVersionRuleModifier,
    AppVersionRulePlatform,
    AppVersionRuleStatus,
} from '../../../common/appVersionRule/appVersionRuleTypes';

type AdminDashboardAppVersionsFormProps = {
    onComplete: Function;
    url: string;
};

export const AdminDashboardAppVersionsForm = ({
    onComplete,
    url,
}: AdminDashboardAppVersionsFormProps): ReactElement => {
    // form data
    const [platform, setPlatform] = useState(AppVersionRulePlatform.ios);
    const [ruleModifier, setRuleModifier] = useState(AppVersionRuleModifier.olderOrEqual);
    const [version, setVersion] = useState('');
    const [reason, setReason] = useState('');
    const [status, setStatus] = useState(AppVersionRuleStatus.blocked);

    // submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [error, setError] = useState('');

    const onSubmit = useCallback(
        async (event: FormEvent) => {
            event.preventDefault();

            setError('');
            setIsSubmitting(true);

            try {
                const {
                    data: { referral },
                } = await http({
                    method: METHODS.POST,
                    url,
                    data: {
                        platform: platform,
                        modifier: ruleModifier,
                        version: version,
                        reason: reason,
                        status: status,
                    },
                });

                setIsSuccessful(true);
                setIsSubmitting(false);

                await delay(2000);

                onComplete(referral);
            } catch (err) {
                if (err instanceof AxiosError) {
                    setError(err.response?.data.error.message);
                } else if (err instanceof Error) {
                    setError(err.message);
                }
                logger.error('Failed to submit app version rule', err, { platform, ruleModifier, version, status });
                setIsSuccessful(false);
                setIsSubmitting(false);
            }
        },
        [platform, ruleModifier, version, reason, status],
    );

    return (
        <form className="AdminDashboardForm" onSubmit={onSubmit}>
            <div>
                <label className="input-wrapper">
                    <label>
                        Platform:
                        <select
                            value={platform}
                            onChange={(event) => setPlatform(event.target.value as AppVersionRulePlatform)}
                        >
                            <option value={AppVersionRulePlatform.ios}>iOS</option>
                            <option value={AppVersionRulePlatform.android}>Android</option>
                            <option value={AppVersionRulePlatform.electron}>Electron</option>
                            <option value={AppVersionRulePlatform.web}>Web / PWA</option>
                        </select>
                    </label>
                    <label>
                        Modifier:
                        <select
                            value={ruleModifier}
                            onChange={(event) => setRuleModifier(event.target.value as AppVersionRuleModifier)}
                        >
                            <option value={AppVersionRuleModifier.olderOrEqual}>{'<='}</option>
                            <option value={AppVersionRuleModifier.equal}>{'=='}</option>
                            <option value={AppVersionRuleModifier.newerOrEqual}>{'>='}</option>
                        </select>
                    </label>
                    <label htmlFor="versionNumber">App Version Rule to Add</label>
                    <input
                        id="versionNumber"
                        name="versionNumber"
                        value={version}
                        onChange={(e) => setVersion(e.target.value)}
                    />
                    <label htmlFor="reason">Reason</label>
                    <input id="reason" name="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                    <label>
                        Status:
                        <select
                            value={status}
                            onChange={(event) => setStatus(event.target.value as AppVersionRuleStatus)}
                        >
                            <option value={AppVersionRuleStatus.blocked}>Blocked</option>
                            <option value={AppVersionRuleStatus.unsafe}>Unsafe</option>
                        </select>
                    </label>
                </label>
            </div>

            <div className="submit-wrapper">
                {!isSubmitting && !isSuccessful && (
                    <input disabled={isEmpty(version) || isEmpty(reason)} type="submit" className="submit-button" />
                )}
                {isSubmitting && <LoadingSpinner show />}
                {isSuccessful && <span className="success">✔︎ Rule added</span>}
                {error && <span className="error">{error}</span>}
            </div>
        </form>
    );
};

export default AdminDashboardAppVersionsForm;
